<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { router, usePage } from "@inertiajs/svelte";
  import { Plus, Edit3, Trash2, Tag, CornerDownRight, FolderKanban } from "lucide-svelte";
  import { Button } from "$lib/components/ui/button";
  import { Badge } from "$lib/components/ui/badge/index.js";
  import { toast } from "svelte-sonner";
  import { appStore } from '$lib/stores/appStore';

  let { categories = [] } = $props();
  
  const page = usePage();
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.categories || {});

  function deleteCategory(id: number | string, name: string) {
    const confirmMsg = t.confirm_delete?.replace("%{name}", name) || `Are you sure to delete ${name}?`;
    
    if (confirm(confirmMsg)) {
      const toastId = toast.loading(t.toast_deleting || "Processing...");
      router.delete(`/categories/${id}`, {
        onSuccess: () => toast.success(t.toast_delete_success || "Success", { id: toastId }),
        onError: () => toast.error(t.toast_delete_error || "Error", { id: toastId })
      });
    }
  }

    $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });
</script>

<svelte:head>
  <title>{t.title || '分类管理'} | Fentime Aurora</title>
</svelte:head>

<div class="p-8 max-w-5xl mx-auto">
  <header class="flex justify-between items-center mb-8 dark:border-zinc-800 pb-6">
    <div>
      <h1 class="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 flex items-center gap-2.5">
        <Tag class="size-5 text-zinc-800 dark:text-zinc-200" /> {t.title}
      </h1>
      <p class="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
    </div>
    
    <Button 
      onclick={() => router.get('/categories/new')}
    >
      <Plus class="size-4" /> {t.btn_new}
    </Button>
  </header>

  <div class="bg-card border dark:border-zinc-800 rounded-xl overflow-hidden shadow-sm bg-white dark:bg-zinc-900">
    {#if categories.length === 0}
      <div class="p-16 text-center flex flex-col items-center justify-center max-w-sm mx-auto">
        <div class="size-10 bg-zinc-50 dark:bg-zinc-800 border dark:border-zinc-700 rounded-lg flex items-center justify-center mb-4 text-muted-foreground">
          <FolderKanban class="size-5 text-zinc-400" />
        </div>
        <h3 class="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t.empty_title}</h3>
        <p class="text-xs text-muted-foreground mt-1 mb-4">{t.empty_desc}</p>
        <Button size="sm" variant="outline" onclick={() => router.get('/categories/new')} class="text-xs rounded-md">
          {t.empty_action}
        </Button>
      </div>
    {:else}
      <div class="divide-y divide-zinc-100 dark:divide-zinc-800">
        {#each categories as cat}
          <div class="p-4 hover:bg-zinc-50/40 dark:hover:bg-zinc-800/40 transition-colors flex justify-between items-center bg-white dark:bg-zinc-900">
            <div class="flex items-center gap-3">
              <span 
                class="size-3 rounded-full border border-zinc-200 dark:border-zinc-700 shadow-sm" 
                style="background-color: {cat.color || '#e4e4e7'}"
              ></span>
              <div class="flex items-center gap-2">
                <span class="font-medium text-zinc-900 dark:text-zinc-100 text-sm tracking-tight">{cat.name}</span>
                <Badge variant="secondary" class="font-mono text-[10px] px-1.5 py-0 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 rounded border-none shadow-none">
                  {cat.slug}
                </Badge>
              </div>
            </div>
            
            <div class="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                class="size-8 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
                onclick={() => router.get(`/categories/${cat.id}/edit`)}
              >
                <Edit3 class="size-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                class="size-8 text-zinc-400 hover:text-destructive hover:bg-destructive/5 rounded-md"
                onclick={() => deleteCategory(cat.id, cat.name)}
              >
                <Trash2 class="size-4" />
              </Button>
            </div>
          </div>

          {#if cat.children && cat.children.length > 0}
            {#each cat.children as child}
              <div class="p-4 pl-10 bg-zinc-50/40 dark:bg-zinc-900/40 border-t border-zinc-100/80 dark:border-zinc-800/80 hover:bg-zinc-50/80 dark:hover:bg-zinc-800/80 transition-colors flex justify-between items-center">
                <div class="flex items-center gap-2.5 text-zinc-500">
                  <CornerDownRight class="size-3.5 text-zinc-300 dark:text-zinc-700 stroke-[2.5]" />
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-normal text-zinc-800 dark:text-zinc-200">{child.name}</span>
                    <Badge variant="outline" class="font-mono text-[9px] px-1 py-0 text-zinc-400 dark:text-zinc-500 bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 shadow-none">
                      {child.slug}
                    </Badge>
                  </div>
                </div>
                
                <div class="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    class="size-7 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
                    onclick={() => router.get(`/categories/${child.id}/edit`)}
                  >
                    <Edit3 class="size-3.5" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    class="size-7 text-zinc-400 hover:text-destructive hover:bg-destructive/5 rounded-md"
                    onclick={() => deleteCategory(child.id, child.name)}
                  >
                    <Trash2 class="size-3.5" />
                  </Button>
                </div>
              </div>
            {/each}
          {/if}
        {/each}
      </div>
    {/if}
  </div>
</div>